import { Ptc } from "../parsers/ptcParser";
import { Atin } from "../parsers/sthAtinParser";
import {
  fitAndAlignColumns,
  getHeaderToIndexMap,
  getRowsWithProperties,
  getUsedRangeWithProperties,
} from "../utils/excel";
import { isAtinAffected } from "../sth/sth";
import { ExcelValidationError, NoSheetExistsError } from "../utils/ExcelValidationError";
import {
  ANALYSIS_SHEET_COLUMNS_AFTER_STH_JURISDICTIONS,
  ANALYSIS_SHEET_COLUMNS_BEFORE_STH_JURISDICTIONS,
  ANALYSIS_SHEET_NAME,
  ANALYSIS_SHEET_REQUIRED_COLUMNS,
  ATIN_ID_COLUMN,
  ATIN_NAME_COLUMN,
  BLANK_STH_TAXABILITY,
  DEPRECATED_COLUMN,
  FINAL_PTC_COLUMN,
  PTC_ACTION_COLUMN,
  PTC_ATOM_COUNT,
  PTC_NA_COLUMN,
  PTC_TAXABILITY_PATTERN,
  STH_JURISDICTION_COLUMN_SEPARATOR,
  STH_TAXABILITY_SEPARATOR,
  YES_STRING,
} from "../utils/constants";
import { getSheetNameFromDateRange } from "../utils/writerUtils";

const columns = (jurisdictions: string[]): string[] => [
  ...ANALYSIS_SHEET_COLUMNS_BEFORE_STH_JURISDICTIONS,
  ...jurisdictions,
  ...ANALYSIS_SHEET_COLUMNS_AFTER_STH_JURISDICTIONS,
];

export const writeAtinToPtcMappings = async (
  atinToPtcMappings: { [atinId: string]: Ptc },
  atins: Map<string, Atin>,
  startDate: Date,
  endDate: Date,
  sthTypeToJurisdictions: Map<string, string[]>,
  setLoadingText: (loadingText: string) => void
) => {
  await Excel.run(async (context) => {
    setLoadingText("Writing results to file...");

    const sheets = context.workbook.worksheets;
    const analysisSheet = sheets.add(getSheetNameFromDateRange(startDate, endDate, ANALYSIS_SHEET_NAME));
    await context.sync();

    const jurisdictionHeaders = sthJurisdictionsToHeaders(sthTypeToJurisdictions);
    const headers = columns(jurisdictionHeaders);
    for (let column = 0; column < headers.length; column++) {
      analysisSheet.getCell(0, column).values = [[headers[column]]];
    }

    const usedRange = await getUsedRangeWithProperties(context, analysisSheet, ["columnCount"]);
    const headerToIndexMap = await getHeaderToIndexMap(usedRange, context);

    let row = 1;
    for (const [atinId, atin] of atins) {
      analysisSheet.getCell(row, headerToIndexMap.get(DEPRECATED_COLUMN)).untrack().values = [[atin.deprecated ? YES_STRING : ""]];
      analysisSheet.getCell(row, headerToIndexMap.get(ATIN_ID_COLUMN)).untrack().values = [[atinId]];
      analysisSheet.getCell(row, headerToIndexMap.get(ATIN_NAME_COLUMN)).untrack().values = [[atin.atinName]];
      analysisSheet.getCell(row, headerToIndexMap.get(PTC_NA_COLUMN)).untrack().values = [[atin.mappedPtc.ptcName]];
      analysisSheet.getCell(row, headerToIndexMap.get(PTC_ATOM_COUNT)).untrack().values = [[atin.mappedPtc.atinsMapped.length]];
      analysisSheet.getCell(row, headerToIndexMap.get(PTC_TAXABILITY_PATTERN)).untrack().values = [[atin.mappedPtc.taxabilityPatternNum]];
      const ptcAction = getPtcAction(atin, atinToPtcMappings);
      analysisSheet.getCell(row, headerToIndexMap.get(PTC_ACTION_COLUMN)).untrack().values = [[ptcAction]];
      analysisSheet.getCell(row, headerToIndexMap.get(FINAL_PTC_COLUMN)).untrack().values = [
        [ptcAction ? atinToPtcMappings[atinId]?.ptcName ?? atin.mappedPtc.ptcName : ""],
      ];

      const sthTaxabilities = atin.secondaryTaxability.split(STH_TAXABILITY_SEPARATOR);
      for (const [index, jurisdictionHeader] of jurisdictionHeaders.entries()) {
        analysisSheet.getCell(row, headerToIndexMap.get(jurisdictionHeader)).untrack().values = [
          [sthTaxabilities[index] && sthTaxabilities[index] !== BLANK_STH_TAXABILITY ? sthTaxabilities[index] : ""],
        ];
      }

      row++;
    }

    fitAndAlignColumns(usedRange, Excel.HorizontalAlignment.left);
    analysisSheet.activate();
  });
};

export const writePtcReplacement = async (
  worksheet: string,
  ptcToReplace: string,
  ptcToReplaceWith: string,
  isNewPtc: boolean,
  setLoadingText: (loadingText: string) => void
) => {
  return Excel.run(async (context) => {
    setLoadingText("Writing PTC replacement");

    const analysisSheet = context.workbook.worksheets.getItemOrNullObject(worksheet);
    await context.sync();

    if (analysisSheet.isNullObject) {
      throw NoSheetExistsError(worksheet);
    }

    const usedRange = await getUsedRangeWithProperties(context, analysisSheet, ["rowCount", "columnCount"]);

    const headerToIndex = await getHeaderToIndexMap(usedRange, context);
    for (const column of ANALYSIS_SHEET_REQUIRED_COLUMNS) {
      if (!headerToIndex.has(column)) {
        throw new ExcelValidationError(`Missing column: ${column}`, worksheet);
      }
    }

    const atinsChanged: string[] = [];
    const rows = await getRowsWithProperties(usedRange, context, ["values"]);
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];

      const atinId = row.values[0][headerToIndex.get(ATIN_ID_COLUMN)].toString().trim();
      const oldPtc = row.values[0][headerToIndex.get(PTC_NA_COLUMN)].toString().trim();
      const finalPtc = row.values[0][headerToIndex.get(FINAL_PTC_COLUMN)].toString().trim();
      const ptcAction = row.values[0][headerToIndex.get(PTC_ACTION_COLUMN)].toString().trim();

      if (finalPtc !== ptcToReplace || finalPtc === ptcToReplaceWith) continue;

      analysisSheet.getCell(i, headerToIndex.get(FINAL_PTC_COLUMN)).values = [[ptcToReplaceWith]];
      atinsChanged.push(atinId);

      const newPtcAction = getPtcActionFromReplacement(oldPtc, ptcToReplaceWith, isNewPtc);
      if (newPtcAction !== ptcAction) {
        analysisSheet.getCell(i, headerToIndex.get(PTC_ACTION_COLUMN)).values = [[newPtcAction]];
      }
    }

    return atinsChanged;
  });
};

const getPtcAction = (atin: Atin, atinToPtc: { [atinId: string]: Ptc }) => {
  const atinId = atin.atinId;
  if (!(atinId in atinToPtc) || atinToPtc[atinId].ptcName === atin.mappedPtc.ptcName) {
    if (isAtinAffected(atin)) {
      return "Keep";
    } else {
      return "";
    }
  } else if (atinToPtc[atinId].isNew) {
    return "New";
  } else {
    return "Reuse";
  }
};

const getPtcActionFromReplacement = (oldPtc: string, newPtc: string, isNew: boolean) => {
  if (oldPtc === newPtc) {
    return "Keep";
  } else if (isNew) {
    return "New";
  } else {
    return "Reuse";
  }
};

const sthJurisdictionsToHeaders = (sthTypeToJurisdictions: Map<string, string[]>) => {
  const columns: string[] = [];
  for (const [sthType, jurisdictions] of sthTypeToJurisdictions) {
    columns.push(...jurisdictions.map((jurisdiction) => sthJurisdictionToHeader(sthType, jurisdiction)));
  }
  return columns;
};

const sthJurisdictionToHeader = (sthType: string, jurisdiction: string) => {
  return sthType + STH_JURISDICTION_COLUMN_SEPARATOR + jurisdiction;
};
